/**
 * Personalization Handler
 * Handles adding personalized products to cart with line item properties
 */

class PersonalizationHandler {
  constructor() {
    this.init();
  }

  init() {
    // Find all personalized add-to-cart buttons
    const buttons = document.querySelectorAll('.add-to-cart-personalized');
    buttons.forEach(button => {
      button.addEventListener('click', (e) => this.handleAddToCart(e));
    });

    // Lock quantity to 1 when a personalization form is present
    this.lockQuantity();
  }

  lockQuantity() {
    const form = document.querySelector('.personalization-form');
    if (!form) return;

    // Find and lock all quantity inputs on the page
    const qtyInputs = document.querySelectorAll('input[name="quantity"], .quantity__input');
    qtyInputs.forEach(input => {
      input.value = 1;
      input.min = 1;
      input.max = 1;
      input.readOnly = true;
      input.style.pointerEvents = 'none';
      input.style.opacity = '0.6';
    });

    // Disable the +/- quantity buttons
    const qtyButtons = document.querySelectorAll('.quantity__button');
    qtyButtons.forEach(btn => {
      btn.disabled = true;
      btn.style.opacity = '0.4';
      btn.style.pointerEvents = 'none';
    });
  }

  async handleAddToCart(event) {
    const button = event.target;
    const productType = button.dataset.type;
    const variantId = button.dataset.variantId;

    // Disable button during processing
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding...';

    try {
      // Collect personalization data based on product type
      const properties = await this.collectProperties(productType);

      if (!properties) {
        throw new Error('Please fill in all required fields');
      }

      // Add to cart via Ajax
      await this.addToCart(variantId, properties);

      // Show success
      button.textContent = '✓ Added!';
      button.style.background = '#2e7d32';

      // Redirect to cart after short delay
      setTimeout(() => {
        window.location.href = '/cart';
      }, 1000);

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError(error.message);
      
      // Reset button
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  async collectProperties(productType) {
    const properties = {};

    // Collect legacy product-type-specific fields
    switch (productType) {
      case 'cutting-board':
        return this.collectCuttingBoardProperties();
      
      case 'easter-tag':
        return this.collectEasterTagProperties();
      
      case 'mug-upload':
        return await this.collectMugUploadProperties();

      default:
        // Fall through to tag-based collection below
        break;
    }

    // Collect tag-based individual fields (properties[Key] inputs)
    const tagFieldProperties = this.collectTagBasedProperties();

    // If no tag-based fields were found either, throw an error
    if (Object.keys(tagFieldProperties).length === 0) {
      throw new Error('Unknown product type');
    }

    return tagFieldProperties;
  }

  /**
   * Collects all tag-based personalization fields rendered by the Liquid snippet.
   * Each input uses name="properties[Key]" and data-property-key="Key".
   * Returns an object of { 'Key': 'value', ... } ready for the cart API.
   */
  collectTagBasedProperties() {
    const properties = {};
    const fields = document.querySelectorAll('.personalization-tag-field');

    fields.forEach(field => {
      const key = field.dataset.propertyKey;
      const value = field.value ? field.value.trim() : '';

      if (!key) return;

      if (field.hasAttribute('required') && !value) {
        throw new Error(`Please fill in the "${key}" field`);
      }

      if (value) {
        properties[key] = value;
      }
    });

    return properties;
  }

  collectCuttingBoardProperties() {
    const lastName = document.getElementById('last-name')?.value.trim();
    const estDate = document.getElementById('est-date')?.value.trim();

    // Validation
    if (!lastName || !estDate) {
      throw new Error('Please enter both Last Name and Established Date');
    }

    if (lastName.length > 30) {
      throw new Error('Last Name must be 30 characters or less');
    }

    if (estDate.length > 20) {
      throw new Error('Established Date must be 20 characters or less');
    }

    // Check for invalid characters
    if (this.hasInvalidChars(lastName) || this.hasInvalidChars(estDate)) {
      throw new Error('Invalid characters detected. Please use only letters, numbers, and basic punctuation');
    }

    return {
      'Last Name': lastName,
      'Established Date': estDate,
      '_Product Type': 'Cutting Board' // Hidden property for internal use
    };
  }

  collectEasterTagProperties() {
    const firstName = document.getElementById('first-name')?.value.trim();

    // Validation
    if (!firstName) {
      throw new Error('Please enter a First Name');
    }

    if (firstName.length > 20) {
      throw new Error('First Name must be 20 characters or less');
    }

    if (this.hasInvalidChars(firstName)) {
      throw new Error('Invalid characters detected. Please use only letters');
    }

    return {
      'First Name': firstName,
      '_Product Type': 'Easter Basket Tag' // Hidden property
    };
  }

  async collectMugUploadProperties() {
    const dropboxLink = document.getElementById('dropbox-link')?.value;

    // Validation
    if (!dropboxLink) {
      throw new Error('Please upload a design file first');
    }

    // Get file info from session storage
    const fileInfo = sessionStorage.getItem('dropbox_upload_info');
    let fileName = 'design-file';
    
    if (fileInfo) {
      try {
        const info = JSON.parse(fileInfo);
        fileName = info.fileName || fileName;
      } catch (e) {
        console.error('Error parsing file info:', e);
      }
    }

    return {
      'Design File': dropboxLink,
      'File Name': fileName,
      '_Product Type': 'Sublimation Mug' // Hidden property
    };
  }

  hasInvalidChars(text) {
    // Allow letters, numbers, spaces, and basic punctuation
    const validPattern = /^[a-zA-Z0-9\s\-'.&,!]+$/;
    return !validPattern.test(text);
  }

  async addToCart(variantId, properties) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: properties
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add to cart');
    }

    return await response.json();
  }

  showError(message) {
    const errorContainer = document.getElementById('personalization-error');
    const errorText = errorContainer?.querySelector('.error-text');
    
    if (errorContainer && errorText) {
      errorText.textContent = message;
      errorContainer.style.display = 'flex';
      
      // Auto-hide after 5 seconds
      setTimeout(() => {
        errorContainer.style.display = 'none';
      }, 5000);
    } else {
      alert(message);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PersonalizationHandler();
  });
} else {
  new PersonalizationHandler();
}

// Export for use in other scripts
window.PersonalizationHandler = PersonalizationHandler;
